import { Button, InlineGrid, Text, TextField } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import Currency from "components/format/currency/currency";

type Props = {
  quantity: string;
  price: string;
  notes: string;
  onQuantityChange: (quantity: string) => void;
  onPriceChange: (price: string) => void;
  onNotesChange: (notes: string) => void;
};

/** Passes a typed number on only while it is digits with at most one point; a comma counts as the point. */
function acceptNumber(typed: string, accept: (value: string) => void) {
  const normalized = typed.replace(",", ".");
  if (/^(\d+)?(\.)?(\d*)?$/.test(normalized)) accept(normalized);
}

/**
 * The fields of one product or product-group line: quantity and price, the
 * resulting sum, and a note. Two columns on a phone (quantity and price, then
 * sum and note), four from `lg` — four equal columns leave a phone about 50 px
 * a field.
 */
export function ProductLineFields({
  quantity,
  price,
  notes,
  onQuantityChange,
  onPriceChange,
  onNotesChange,
}: Props) {
  const { t } = useTranslation();
  return (
    <InlineGrid gap="400" columns={{ xs: 2, lg: 4 }}>
      <TextField
        label={t("quantity")}
        type="text"
        value={quantity}
        autoComplete="off"
        onChange={(typed) => acceptNumber(typed, onQuantityChange)}
      />
      <TextField
        label={t("price")}
        type="text"
        autoComplete="off"
        value={price}
        onChange={(typed) => acceptNumber(typed, onPriceChange)}
      />
      <div>
        <Text variant="bodyMd" as="p">
          {t("totalPrice")}
        </Text>
        <Currency
          isDecimal={true}
          value={(parseFloat(quantity) || 0) * (parseFloat(price) || 0)}
          isSymbol={true}
        />
      </div>
      <TextField
        label={t("notes")}
        type="text"
        autoComplete="off"
        value={notes}
        onChange={onNotesChange}
      />
    </InlineGrid>
  );
}

/**
 * Takes a product line out of the list. Always drawn: a ResourceItem's own
 * shortcut actions appear only on hover and only from 1040 px, so a phone or a
 * tablet had no way to remove a line.
 */
export function RemoveLineButton({ onRemove }: { onRemove: () => void }) {
  const { t } = useTranslation();
  return (
    <Button variant="plain" tone="critical" size="slim" onClick={onRemove}>
      {t("remove")}
    </Button>
  );
}
